/**
 * mergeLoadedConnection — merge a freshly-rebuilt connection record onto what the
 * context already holds, preferring whichever source has data for each field.
 *
 * `loadConnectionData` rebuilds a connection's whole entry from the phone's local
 * copy and owns most fields. A handful of fields are owned by OTHER load paths
 * that run independently and must not be dropped by this rebuild:
 *
 *   - `userContacts`, `nickname` — written by `loadUserContacts`/`loadNickname`
 *     straight from `connection_views` (the DB). They are NOT on `fresh`, so
 *     without an explicit carry they vanish whenever a late `loadConnectionData`
 *     rebuild wins the race (return-to-foreground, recovery, remount) while the
 *     DB still holds them. `displayName` was already carried this way; these two
 *     were the gap — an added phone/note showed, then blanked on return
 *     (detail-pass-2 #3). The on-screen Note reads `nickname`, so carrying it
 *     fixes the Note too.
 *
 * Pure: no React, no storage. Unit-tested in connectionMerge.test.js.
 *
 * @param {object} fresh - the rebuilt record (loadConnectionData's connectionData)
 * @param {object} [existing] - prev[chatRoomId]
 * @returns {object} the merged record to store at [chatRoomId]
 */
export const mergeLoadedConnection = (fresh, existing = {}) => ({
  ...fresh,
  profileData: fresh.profileData || existing.profileData || null,
  anonData: fresh.anonData || existing.anonData || null,
  connectionMetadata: fresh.connectionMetadata || existing.connectionMetadata || null,
  // Prefer context over localStorage for chatMessages: addChatMessage writes
  // optimistic messages to context, and the localStorage snapshot read by
  // getChatMessages may predate that write. The DB sync is the sole authority
  // for replacing them.
  chatMessages: existing.chatMessages !== undefined ? existing.chatMessages : fresh.chatMessages,
  notes: fresh.notes || existing.notes || '',
  hasSharedContactInfo: fresh.hasSharedContactInfo || existing.hasSharedContactInfo,
  hasAddedToContacts: fresh.hasAddedToContacts || existing.hasAddedToContacts,
  displayName: fresh.displayName || existing.displayName || null,
  // DB-first fields loaded by their own paths — carry them across the rebuild
  // (fresh never has them today), kept in the same `fresh || existing` form as
  // displayName so a future fresh value would win instead of being dropped.
  userContacts: fresh.userContacts || existing.userContacts,
  nickname: fresh.nickname || existing.nickname,
});
